import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { IconEye, IconPencil, IconX } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { trackPromise } from "react-promise-tracker";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import GridComponent from "../../shared-components/gridComponent";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import StateCityModal from "./StateCityModal";

const StateAndCityList = () => {
  const navigate = useNavigate();
  const [gridKey, setGridKey] = useState(false);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const refreshGrid = () => setGridKey((prev) => !prev);

  const columns = [
    {
      field: "stateName",
      headerName: "State",
      flex: 1,
      sortable: true,
    },
    {
      field: "cities",
      headerName: "Cities",
      flex: 2,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEdit(params.row)}>
            <IconPencil stroke={1.5} />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row)}>
            <IconX stroke={1.5} />
          </IconButton>
        </>
      ),
    },
  ];

  const getGridData = async ({ page, pageSize, sortModel, searchText }) => {
    const sort =
      sortModel.length > 0
        ? {
            sortColumnName: sortModel[0].field,
            sortOrder: sortModel[0].sort === "asc" ? "asc" : "desc",
          }
        : {
            sortColumnName: "",
            sortOrder: "",
          };

    const requestObject = {
      pageNumber: page,
      rowsPerPage: pageSize,
      searchColumns: {
        searchedColumn: "",
        searchText: "",
        columnDataType: "",
      },
      sortColumns: sort,
      searchText: searchText,
    };

    try {
      const response = await trackPromise(
        apiClient.post(urlConstant.State.GetAllStateAndCity, requestObject, {
          authRequired: true,
        })
      );

      if (response?.status !== dataConstant.APIStatus.Success) {
        showSnackbar(response?.message || "Failed to fetch data", "error");
        return { items: [], totalCount: 0 };
      }

      const decryptedData = response.data?.list?.map((item) => ({
        ...item,
        id: item.unqGuid,
      }));
      return {
        items: decryptedData || [],
        totalCount: response.data?.totalCount || 0,
      };
    } catch (error) {
      showSnackbar("Error fetching data", "error");
      return { items: [], totalCount: 0 };
    }
  };
  const handleDelete = (row) => {
    setDeleteRow(row);
    setDeleteDialogOpen(true);
  };
  const confirmDelete = async () => {
    try {
      const response = await trackPromise(
        apiClient.delete(
          `${urlConstant.State.DeleteStateAndCity}?stateUnqGuid=${deleteRow.unqGuid}`,
          {
            authRequired: true,
          }
        )
      );

      if (response?.status === dataConstant.APIStatus.Success) {
        showSnackbar("Deleted successfully", "success");
        refreshGrid();
      } else {
        showSnackbar(response?.message || "Failed to delete", "error");
      }
    } catch (error) {
      showSnackbar("Error occurred while deleting", "error");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteRow(null);
    }
  };
  const handleAdd = () => {
    setSelectedRow(null);
    setOpenPopup(true);
  };
  const handleEdit = async (row) => {
    try {
      const response = await trackPromise(
        apiClient.get(
          `${urlConstant.State.GetStateAndCityByStateId}?stateUnqGuid=${row.unqGuid}`,
          {
            authRequired: true,
          }
        )
      );

      if (response?.statusCode === 200 && response?.status !== "Failure") {
        const { stateName, cityViewResponses } = response.data;
        const parsedCities = cityViewResponses.map((city) => ({
          id: city.unqGuid,
          name: city.cityName,
          isDeleted: false,
        }));
        setSelectedRow({
          id: row.unqGuid,
          stateName,
          cities: parsedCities,
        });
        setOpenPopup(true);
      } else {
        showSnackbar(
          response?.message || "Failed to fetch state and city data",
          "error"
        );
      }
    } catch (error) {
      showSnackbar("Error fetching state and city data", "error");
    }
  };

  return (
    <>
      <GridComponent
        columns={columns}
        getGridData={getGridData}
        gridKey={gridKey}
        isAddButton={true}
        addButtonTitle="Add State - City"
        onAdd={handleAdd}
      />

      <StateCityModal
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        initialData={selectedRow || {}}
        onSave={() => {
          refreshGrid();
          setOpenPopup(false);
        }}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
      >
        <DialogContent>
          <DialogContentText
            typography="h5"
            color="default.main"
            textAlign="center"
          >
            Are you sure you want to delete{" "}
            <strong>{deleteRow?.stateName}</strong>?
          </DialogContentText>
          <DialogActions
            sx={{ padding: 0, justifyContent: "center", mt: 4, border: "none" }}
          >
            <Button size="small" onClick={confirmDelete} variant="contained">
              Yes
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
            >
              No
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <CommonSnackBar
        snackObj={snack}
        closeSnack={closeSnack}
        duration={2000}
      />
    </>
  );
};

export default StateAndCityList;
